import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Row, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { QaCategoryService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { useTranslation } from "react-i18next";
import FormErrorSummary from "../../shared/components/FormErrorSummary";

const schema = yup.object().shape({
    title: yup.string().required('formErrors.titleRequired')
});

interface QaCategoryState {
    title: string;
}

const QaCategoryForm: React.FC = () => {
    const { t } = useTranslation(); // Initialize translation hook
    const { id } = useParams<{ id: string }>();
    const isEdit = Boolean(id);
    const [qaCategoryData, setQaCategoryData] = useState<QaCategoryState | null>(null);
    const navigate = useNavigate();
    const { control, handleSubmit, reset, formState: { errors } } = useForm<QaCategoryState>({
        resolver: yupResolver(schema),
        defaultValues: {
            title: ""
        }
    });

    useEffect(() => {
        if (isEdit) {
            const fetchQaCategory = async () => {
                try {
                    const response = await QaCategoryService.getSingle(+id!);
                    const qaCategory = response;
                    setQaCategoryData({
                        title: qaCategory.title
                    });
                } catch (error) {
                    ErrorToast(error);
                }
            };

            fetchQaCategory();
        }
    }, [isEdit, id]);

    useEffect(() => {
        if (qaCategoryData) {
            reset(qaCategoryData);
        }
    }, [qaCategoryData, reset]);

    const onSubmit = async (data: QaCategoryState) => {
        try {
            isEdit
                ? await QaCategoryService.update(data, +id!)
                : await QaCategoryService.create(data);

            SuccessToast(t(`qaCategory.${isEdit ? 'successUpdate' : 'successCreate'}`));
            navigate('/qa-categories');
            reset();
        } catch (error) {
            ErrorToast(error);
        }
    };

    return (
        <React.Fragment>
            <Row>
                <Col sm={12}>
                    <Card>
                        <CardBody>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <FormErrorSummary errors={errors} />
                                <Row className="g-3">
                                    <Col sm={12} xl={6}>
                                        <div className="mb-0">
                                            <label className="form-label">{t("qaCategory.title")}</label>
                                            <Controller
                                                name="title"
                                                control={control}
                                                render={({ field }) => (
                                                    <input
                                                        type="text"
                                                        className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                                                        placeholder={t("qaCategory.enterTitle")}
                                                        {...field}
                                                    />
                                                )}
                                            />
                                            {errors.title && <div className="invalid-feedback">
                                                {t(errors.title.message as string)}
                                            </div>}
                                        </div>
                                    </Col>
                                    <Col sm={12}>
                                        <Button type="submit" variant="primary">
                                            {t(`qaCategory.${isEdit ? 'update' : 'create'}`)}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default QaCategoryForm;